import { CommonModule } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	computed,
	type ElementRef,
	EventEmitter,
	forwardRef,
	inject,
	input,
	type OnDestroy,
	Output,
	signal,
	viewChild,
	ViewEncapsulation
} from "@angular/core";
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { UploaderService } from "@app/@services/generic/uploader.service";
import { AuthStateService } from "@app/@services/shared/auth-state.service";
import { AuthService } from "@app/@services/shared/auth.service";
import { TranslateModule } from "@ngx-translate/core";
import { BehaviorSubject, type Subscription } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { ButtonComponent } from "../button/button.component";
import { IconComponent } from "../icon/icon.component";
import { LoaderModule } from "../loader/loader.module";
import { FiletypeIconComponent } from "../misc-icons/filetype-icon/filetype-icon.component";

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => FileComponent),
	multi: true,
};

export interface ImageFormat {
	width: number;
	height: number;
}

export interface CbUploadEvent {
	files: CbFile[];
}

export interface CbFile {
	f?: File;
	progress?: BehaviorSubject<number>;
	name: string;
	url?: string;
	size?: string;
	type: string;
}

@Component({
	selector: "cb-file",
	templateUrl: "./file.component.html",
	styleUrls: ["./file.component.scss"],
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		IconComponent,
		ButtonComponent,
		LoaderModule,
		FiletypeIconComponent,
	],
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	encapsulation: ViewEncapsulation.None,
})
export class FileComponent implements ControlValueAccessor, OnDestroy {
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly authStateService = inject(AuthStateService);

	readonly inputFile = viewChild<ElementRef>("inputFile");

	public errorsMap: { [key: string]: string } = {
		format: "COMMON.FILE.ERRORS.FORMAT",
		size: "COMMON.FILE.ERRORS.SIZE",
		dimensions: "COMMON.FILE.ERRORS.DIMENSIONS",
		multiple: "COMMON.FILE.ERRORS.MULTIPLE",
		maxFiles: "COMMON.FILE.ERRORS.MAX_FILES",
	};
	public selectionError = signal<string>("");

	public uploaderService = inject(UploaderService);
	public authService = inject(AuthService);

	// Inputs from parent
	public readonly label = input<string>();
	public readonly placeholder = input<string>();
	public readonly accept = input<string>();
	public readonly multiple = input<boolean>();
	public readonly required = input(false);
	public readonly fileTypes = input<string[]>();
	public readonly maxFileSize = input<number>();
	public readonly maxFiles = input<number>();
	public readonly imageFormat = input<ImageFormat>();
	public readonly disabled = input<boolean>();
	public readonly error = input<string>();
	public readonly imageRatio = input<string>();

	public readonly showDropArea = input<boolean>(true);
	public readonly bucketName = input<string>();
	public readonly uploadPath = input("");
	public readonly generateRandomName = input(false);
	public readonly generateRandomSubfolder = input(false);
	public readonly addTenantSubfolder = input(false);
	public readonly deleteRemovedFiles = input(true);
	public readonly uploadToStorage = input(true);
	public readonly filename = input<string>();
	public readonly uploadBehavior = input<("immediate" | "onSubmit" | "none") | undefined>("immediate");

	private innerFiles: CbFile[] = [];

	public toUploadFiles = signal<CbFile[]>([]);
	public toDeleteFiles = signal<CbFile[]>([]);

	@Output() public fileUpload: EventEmitter<CbUploadEvent> =
		new EventEmitter<CbUploadEvent>();
	@Output() public onChange: EventEmitter<CbFile[]> = new EventEmitter<
		CbFile[]
	>();
	@Output() public fileRemove: EventEmitter<CbFile> =
		new EventEmitter<CbFile>();

	// Initialization variables
	private fileChangeSubscription$: Subscription | null;

	public uploadingFiles = signal<CbFile[]>([]);
	public draggingCount = signal<number>(0);

	ngOnInit(): void {
		if (this.bucketName() === undefined && this.uploadToStorage()) {
			throw new Error("bucketName is required when uploadToStorage is true");
		}
	}

	// Functions for Control Value Accessor
	public get files(): CbFile[] {
		return this.innerFiles;
	}

	public set files(v: CbFile[] | CbFile) {
		if (v !== this.innerFiles) {
			let files: CbFile[] = [];
			if (!Array.isArray(v)) {
				files = [v];
				this.onChange.emit([v]);
				this.onChangeCallback(v.url);
			} else {
				if (v.length > 0) {
					files = v;
					this.onChange.emit(files);
					this.onChangeCallback(files.map((f) => f.url));
				} else {
					this.onChange.emit([]);
					this.onChangeCallback(null);
				}
			}

			this.innerFiles = files;
		}
	}

	public writeValue(obj: string[] | string): void {
		let files: CbFile[] = [];

		if (obj) {
			if (!Array.isArray(obj)) {
				const decodedUrl = decodeURIComponent(obj);
				files = [
					{
						name: decodedUrl.split("/").pop() ?? "",
						url: obj,
						type: decodedUrl.split(".").pop() ?? "none",
					},
				];
			} else {
				files = obj.map((f) => {
					const decodedUrl = decodeURIComponent(f);
					return {
						name: decodedUrl.split("/").pop() ?? "",
						url: f,
						type: decodedUrl.split(".").pop() ?? "none",
					};
				});
			}
		}

		this.innerFiles = files;
	}

	private onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	constructor() {
		this.fileChangeSubscription$ = null;
	}

	ngOnDestroy() {
		// Unsubscribe from any observable to avoid memory leaks
		if (this.fileChangeSubscription$) {
			this.fileChangeSubscription$.unsubscribe();
		}
	}

	public errorData = computed(() => {
		const imageFormat = this.imageFormat();
		return {
			maxFiles: this.maxFiles(),
			maxFileSize: `${(this.maxFileSize() ?? 0) / 1024 / 1024} MB`,
			dimensions: `${imageFormat?.width} x ${imageFormat?.height} px`,
			ratio: this.imageRatio(),
			fileTypes: this.fileTypes()?.join(", ") ?? "",
		};
	});

	// Method to get the available file types as string
	public imagePlaceholder1 = computed(() => {
		const fileTypes = this.fileTypes();
		return fileTypes ? fileTypes.join(", ") : "";
	});

	// Method to get image format and ratio as string
	public imagePlaceholder2 = computed(() => {
		let placeholder = "";

		const imageFormat = this.imageFormat();
		if (imageFormat) {
			placeholder += `${imageFormat.width} x ${imageFormat.height} px`;
		}

		const imageRatio = this.imageRatio();
		if (imageRatio) {
			const ratio = imageRatio;
			placeholder += placeholder.length ? ", " : "";
			placeholder += `${ratio}`;
		}

		return placeholder;
	});

	private calculateNearestFileSize(size: number): string {
		const i: number = Math.floor(Math.log(size) / Math.log(1024));
		const val: number = size / 1024 ** i;
		return `${val.toFixed(2)} ${["B", "KB", "MB", "GB", "TB"][i]}`;
	}

	private getUploadFileName(originalFileName: string): string {
		const fileExtension = originalFileName.split(".").pop();

		const filename = this.filename();
		if (filename) {
			return fileExtension
				? `${filename}.${fileExtension}`
				: filename;
		}

		if (this.generateRandomName()) {
			return fileExtension ? `${uuidv4()}.${fileExtension}` : uuidv4();
		}

		return originalFileName;
	}

	private async uploadFiles(files: File[]) {
		if (!files || files.length === 0) return;

		const multiple = this.multiple();
		if (!multiple) {
			if (files.length > 1 || this.uploadingFiles.length > 0) {
				this.selectionError.set("multiple");
				return;
			}
		}

		for (const file of files) {
			const fileName = file.name.toLowerCase();

			const isValid = await this.validateFile(file);
			if (isValid) {
				const subject = new BehaviorSubject<number>(0);

				subject.subscribe((progress) => {
					if (progress === 100) {
						this.uploadingFiles.set(this.uploadingFiles().filter(
							(f) => f.name !== fileName,
						));
						subject.complete();
					}

					this.cdr.detectChanges();
				});

				const uploadingFile: CbFile = {
					f: file,
					name: this.getUploadFileName(fileName),
					size: this.calculateNearestFileSize(file.size),
					type: fileName.split(".").pop() ?? "none",
					progress: subject,
				};

				if (!multiple) {
					for (const f of this.files) {
						this.toDeleteFiles.update((l) => {
							l.push(f);
							return l;
						});
					}
					this.files = [];
					this.toUploadFiles.set([]);
				}

				this.toUploadFiles.update((l) => {
					l.push(uploadingFile);
					return l;
				});

				if (this.uploadBehavior() === "immediate") {
					await this.flush();
				}
			} else {
				this.uploadingFiles.set([]);
				return;
			}
		}
	}

	// Event handler for change in file input element
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public async onFileChange(event: any) {
		const files: File[] = Array.from(event.target.files);
		await this.uploadFiles(files);
		const inputFile = this.inputFile();
		if (inputFile) inputFile.nativeElement.value = "";
	}

	// Event handler for button click
	public onButtonClick(event: Event) {
		if (this.disabled()) {
			return;
		}
		const inputFile = this.inputFile();
		if (inputFile) inputFile.nativeElement.click();
	}

	public onDragEnter(event: DragEvent) {
		event.preventDefault();
		this.draggingCount.set(this.draggingCount() + 1);
	}

	public onDragLeave(event: DragEvent) {
		event.preventDefault();
		this.draggingCount.set(this.draggingCount() - 1);
	}

	// Event handler for dropping a file
	public async onDrop(event: DragEvent) {
		event.preventDefault();
		this.draggingCount.set(0);

		if (event.dataTransfer) {
			const files: File[] = Array.from(event.dataTransfer.files);
			await this.uploadFiles(files);
		}
	}

	public removeFile(file: CbFile): void {
		if (this.toUploadFiles().includes(file)) {
			this.toUploadFiles.set(this.toUploadFiles().filter((f) => f !== file));
		} else {
			if (file) {
				this.onDelete(file);
			}
		}
	}

	// Removes a file from list
	public onDelete(file: CbFile) {
		if (file) {
			this.files = this.files.filter((f) => f !== file);

			if (this.deleteRemovedFiles()) {
				if (this.uploadBehavior() === "immediate") {
					this.deleteFile(file);
				} else {
					this.toDeleteFiles.update((l) => {
						l.push(file);
						return l;
					});
				}
			}

			this.fileRemove.emit(file);
		}
	}

	// Validates file type
	private validateFileTypes(file: File): boolean {
		const fileExtension = file.name.split(".").pop()?.toLowerCase();
		const fileTypes = this.fileTypes();
		return (
			!fileTypes ||
			(fileExtension !== undefined && fileTypes.includes(fileExtension))
		);
	}

	// Validates file size
	private validateFileSize(file: File): boolean {
		const maxFileSize = this.maxFileSize();
		return !maxFileSize || file.size <= maxFileSize;
	}

	// Validates total number of files
	private validateTotalFiles(): boolean {
		const maxFiles = this.maxFiles();
		return !maxFiles || this.files.length < maxFiles; // Notice: it's size because Map object has size property, not length.
	}

	// Validates image ratio
	private validateImageRatio(file: File): Promise<boolean> {
		const imageRatio = this.imageRatio();
		if (!imageRatio || !file.type.startsWith("image")) {
			return Promise.resolve(true);
		}

		// Determine what the width and height should be
		const [width, height] = imageRatio.split(":").map(Number);

		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => {
				const isValid = img.width / img.height === width / height;
				URL.revokeObjectURL(img.src);
				resolve(isValid);
			};
			img.src = URL.createObjectURL(file);
		});
	}

	// Validates image format
	private validateImageFormat(file: File): Promise<boolean> {
		if (!this.imageFormat() || !file.type.startsWith("image")) {
			return Promise.resolve(true);
		}
		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => {
				const imageFormat = this.imageFormat();
				const isValid =
					img.width === imageFormat?.width &&
					img.height === imageFormat?.height;
				URL.revokeObjectURL(img.src);
				resolve(isValid);
			};
			img.src = URL.createObjectURL(file);
		});
	}

	// Wrapper function to validate file
	private validateFile(file: File): Promise<boolean> {
		// biome-ignore lint/suspicious/noAsyncPromiseExecutor: <explanation>
		return new Promise(async (resolve) => {
			const e = (error: string) => {
				this.selectionError.set(error);
				resolve(false);
				return;
			};

			const isTypeValid = this.validateFileTypes(file);
			if (!isTypeValid) e("format");

			const isSizeValid = this.validateFileSize(file);
			if (!isSizeValid) e("size");

			const isMaxFilesValid = this.validateTotalFiles();
			if (!isMaxFilesValid) e("maxFiles");

			const isFormatValid = await this.validateImageFormat(file);
			const isRatioValid = await this.validateImageRatio(file);
			resolve(isFormatValid && isRatioValid);
		});
	}

	public open(url: string) {
		window.open(url, "_blank");
	}

	public async uploadApi(file: CbFile) {
		try {
			if (!file.f) return;

			let path = "";
			if (this.addTenantSubfolder()) {
				if (!this.authStateService.currentTenantId())
					throw new Error("No tenant id available");
				path = this.authStateService.currentTenantId()!;
			}

			if (this.uploadPath().length > 0) {
				path = path.length > 0 ? `${path}/${this.uploadPath()}` : this.uploadPath();
			}

			if (this.generateRandomSubfolder()) {
				path = path.length > 0 ? `${path}/${uuidv4()}` : uuidv4();
			}

			const bucketName = this.bucketName();
			if (bucketName && this.uploadToStorage()) {
				this.uploadingFiles.update((l) => {
					l.push(file);
					return l;
				});
				const uploadPath = await this.uploaderService.uploadFile(
					bucketName,
					path,
					file.name,
					file.f,
					file.progress,
				);

				file.url = uploadPath;

				this.uploadingFiles.set(this.uploadingFiles().filter(
					(f) => f.name !== file.name,
				));
			}

			if (this.multiple()) {
				this.files = [...this.files, file];
			} else {
				this.files = file;
			}

			this.fileUpload.emit({ files: this.files });
		} catch (error) {
			//CbToastService.instance.showHttpError(error);
			this.files = this.files.filter((f) => f.name !== file.name);
			this.uploadingFiles.set(this.uploadingFiles().filter(
				(f) => f.name !== file.name,
			));
			throw error;
		}
	}

	public async deleteFile(file: CbFile) {
		const bucketName = this.bucketName();
		if (!file.url || !bucketName) return;
		if (!this.uploadToStorage()) return;

		await this.uploaderService.deleteFile(bucketName, file.url);
	}

	public async flush(): Promise<void> {
		const deletePromises: Promise<void>[] = [];
		for (const f of this.toDeleteFiles()) {
			deletePromises.push(this.deleteFile(f));
		}
		this.toDeleteFiles.set([]);
		await Promise.all(deletePromises);

		const uploadPromises: Promise<void>[] = [];
		for (const f of this.toUploadFiles()) {
			uploadPromises.push(this.uploadApi(f));
		}

		this.toUploadFiles.set([]);
		await Promise.all(uploadPromises);
	}
}
